import type { NavigateFunction } from "react-router-dom";
import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export type MailTestMode = "auth" | "send";

export interface MailTestStage {
  stage: "config" | "connect" | "tls" | "auth" | "send";
  ok: boolean;
  /** With `ok`: not applicable (no TLS, no credentials). Without: not reached. */
  skipped: boolean;
  smtp_code: number | null;
  /** Relay answer or local error, redacted by back/ — never a key. */
  detail: string | null;
  duration_ms: number | null;
}

export interface SmtpConfig {
  host: string | null;
  port: number | null;
  user: string | null;
  sender: string | null;
  use_ssl: boolean;
  use_starttls: boolean;
  password_set: boolean;
}

export interface SendgridKeyStatus {
  configured: boolean;
  /** `null` = could not tell (network, 403…), never « invalid ». */
  valid: boolean | null;
  has_mail_send: boolean | null;
  scopes_count: number | null;
  http_status: number | null;
  error: string | null;
}

/** First 8 hex chars of the SHA-256 of each key. */
export interface KeyFingerprints {
  smtp_password: string | null;
  sendgrid_api_key: string | null;
  same_key: boolean | null;
}

export interface MailTestResult {
  mode: MailTestMode;
  ok: boolean;
  /** `ok` | `sent` | auth_failed | network_failed | rejected | failed | not_configured | credentials_missing */
  outcome: string;
  stages: MailTestStage[];
  smtp: SmtpConfig;
  sendgrid_api_key: SendgridKeyStatus;
  fingerprints: KeyFingerprints;
  recipient: string | null;
  message_id: string | null;
}

/**
 * Run the mail diagnostic through backoffice (`POST /api/admin/mail/test`).
 *
 * `auth` logs in to the SMTP relay without sending; `send` also sends a test
 * email to `to` (backoffice defaults it to the logged-in admin). A failing
 * relay is a normal result (`ok: false`); only refusals of the request itself
 * (non-admin, recipient outside the internal domains, 5/min) throw.
 */
export async function runMailTest(
  navigate: NavigateFunction,
  mode: MailTestMode,
  to?: string
): Promise<MailTestResult> {
  const body: { mode: MailTestMode; to?: string } = { mode };
  if (mode === "send" && to) body.to = to;
  const res = await apiFetch(
    `${API_URL}/admin/mail/test`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    navigate
  );
  if (!res?.ok) {
    const data = await res?.json().catch(() => null);
    throw new Error(
      typeof data?.detail === "string" ? data.detail : "Échec du diagnostic e-mail"
    );
  }
  return res.json();
}
